import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { DomSanitizer } from "@angular/platform-browser";
import { StorageService, UserService, CommunityService, WindowService, HelperService, ContentService, ProductService, MessageEmbed } from "@mypxplat/xplat/core";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { BehaviorSubject, forkJoin, interval, Subject } from "rxjs";
import { map, skip, takeUntil, throttle } from "rxjs/operators";
import { BetaService } from "./beta.service";
import { FirebaseService } from "./firebase.service";

@Injectable()
export class WebCommunityService extends CommunityService {
  private scripts: any = {};

  public attachingExistingFile: any;
  public attachingPublicFileLink: any;

  public activeThreadMessages: Array<any>;
  public activeThreadMessagesSubscription: any;
  public threadMessageEvent$: Subject<string> = new Subject();

  // cache profile results so we can display them when navigating back.
  public activeProfileResults: Array<any>;
  public activeThread: any;
  public missedMessageSubscription: any;
  public unreadCounts: any = {
    threads: {},
    total: 0,
  };

  public viewing: any = {};

  public userVideoData: Array<any>;
  public userVideoDataMap: any = {};

  public channelSplashes: any = {
    learning_studio_one:
      '<div class="trans-bg p-10 m-t-10 m-b-10 border-radius10" style="max-width: 600px; margin-left: auto; margin-right: auto;">Welcome to the Learning Studio One channel. Use this space to ask questions or share your knowledge about the world\'s most versatile DAW.</div></div>',
    collaboration:
      '<div style="text-align: center; margin-bottom: 30px;"><div class="trans-bg p-10 m-t-10 m-b-10 border-radius10" style="max-width: 600px; margin-left: auto; margin-right: auto;">Welcome to the Collaboration channel. Use this space to post work in progress and invite others to collaborate. Studio One Pro+ lets you manage everything in one place with workspaces, find out how: <br /><br /><span class="btn btn-primary" id="youtubelink" data-youtube-id="WzFkWEzpiNs">Watch a Video</span></div></div>',
  };

  public notificationSubscription: any;
  public migratingPostMessage: string;
  public betaAuthorList: any;
  public betaAuthorMap: any;
  public betaTagCountMap: any;
  public selectedChannel: any;
  public displayedPostWatchers: any[] = [];

  constructor(
    http: HttpClient,
    private _storageService: StorageService,
    private _windowService: WindowService,
    private _sanitizer: DomSanitizer,
    public helperService: HelperService,
    public contentService: ContentService,
    public db: AngularFirestore,
    public storageService: StorageService,
    public userService: UserService,
    public betaService: BetaService,
    public productService: ProductService,
    public fbService: FirebaseService
  ) {
    super(http, _storageService, _windowService, helperService, userService);
  }

  updateProfile(args) {
    return new Promise((resolve, reject) => {
      if (this.profile) {
        this.fbService
          .handleFirestorePromise(() =>
            this.db
              .collection("user_profiles")
              .doc(this.userService.user.id)
              .update({
                ...args,
              })
          )
          .then((result) => {
            this.profile = this.profile ? { ...this.profile, ...args } : args;

            satelliteDocumentUpdates();
          })
          .catch((err) => {
            reject(new Error(err?.message || "An error occurred while updating your profile."));
          });
      } else {
        this.fbService
          .handleFirestorePromise(() => this.db.collection("user_profiles").doc(this.userService.user.id).ref.get())
          .then((result) => {
            let method = "update";
            if (!result.exists) method = "set";
            this.db
              .collection("user_profiles")
              .doc(this.userService.user.id)
              [method]({
                ...args,
              })
              .then((result) => {
                this.profile = this.profile ? { ...this.profile, ...args } : args;
                if (!this.hasSetupCommunity) this.hasSetupCommunity = true;
                satelliteDocumentUpdates();
              })
              .catch((err) => {
                reject(new Error(err?.message || "An error occurred while updating your profile."));
              });
          })
          .catch((err) => {
            reject(new Error(err?.message || "An error occurred while updating your profile."));
          });
      }

      const satelliteDocumentUpdates = () => {
        if (args.firstName || args.lastName || args.photo) {
          this.fbService
            .handleFirestorePromise(() => this.db.collection("user_profiles").doc(this.userService.user.id).collection("linked_users").ref.get())
            .then((result) => {
              if (result.docs.length) {
                let promises = [];
                let updateArgs: any = {};
                if (args.firstName || args.lastName) updateArgs.name = (args.firstName || this.userService.user.firstName) + " " + (args.lastName || this.userService.user.lastName);
                if (args.photo) updateArgs.photo = args.photo;
                let batch = this.db.firestore.batch();
                result.docs.forEach((item) => {
                  batch.update(this.db.collection("user_profiles").doc(item.id).collection("linked_users").doc(this.userService.user.id).ref, updateArgs);
                });
                batch.commit().then((result) => {
                  resolve(true);
                });
              }
            });
        } else {
          resolve(true);
        }
        resolve(true);
      };
    });
  }

  updateCommunityStatus(id, status) {
    return this.fbService.handleFirestorePromise(() => this.db.collection("user_profiles").doc(id).update({ status: status }));
  }

  getFirebaseProfile(id?) {
    if (!id && !this.userService?.user?.id) {
      return Promise.resolve(false);
    }
    return this.fbService
      .handleFirestorePromise(() =>
        this.db
          .collection("user_profiles")
          .doc(id || this.userService.user.id)
          .ref.get()
      )
      .then((result) => {
        if (result.exists) {
          let userData: any = result.data();
          this.profile = this.buildCommunityUserData(userData);
          this.hasSetupCommunity = userData.hasSetupCommunity;
          this.firebaseProfile$.next(this.profile);
          return userData;
        } else {
          return false;
        }
      });
  }

  addUserSkills(data, string_ids?) {
    this.fbService.handleFirestorePromise(() => this.db.collection("user_profiles").doc(this.userService.user.id).update({ skillsQueryMap: string_ids }));
    return super.addUserSkills(data);
  }

  getUserProfile(userId?) {
    return super.getUserProfile(userId, this.fbService.doc(`user_profiles/${userId}`).ref);
  }

  refreshUserProfile(userId?) {
    return super.getUserProfile(userId, this.fbService.doc(`user_profiles/${userId}`).ref, true);
  }

  findMembersBySkills(skills, limit = 2000) {
    return super.findMembersBySkills(skills, limit, this.fbService.collection("user_profiles").ref);
  }

  findMembersByName(skills) {
    return super.findMembersByName(skills, this.fbService.collection("user_profiles"));
  }

  findMembers(skills?, nameQuery?, emailQuery?, limit = 5, experts?) {
    return super.findMembers(this.fbService.collection("user_profiles").ref, skills, nameQuery, emailQuery, limit, experts);
  }

  findModeratedUsers() {
    return super.findModeratedUsers(this.fbService.collection("user_profiles").ref);
  }

  addPost(data, collection: string = "community_posts") {
    return super.addPost(data, this.fbService.collection(collection));
  }

  getThreadMessages(type, thread?, limit = 100, page = 1) {
    return new Promise((resolve, reject) => {
      let messages = [];
      let method;
      if (type == "connection_thread" && thread) {
        method = this.fbService.collection(`connection_threads/${thread.id}/messages`).ref.orderBy("timestamp").limitToLast(100);
      }
      this.fbService.handleFirestorePromise(() => {
        return method.get().then((result) => {
          result.docs.forEach((doc) => {
            let msg: any = doc.data();
            msg.key = doc.id;
            msg.id = doc.id;
            if (msg.file) msg.file.url = this.buildFileUrl(msg.file.user_id, msg.file);
            if (msg.message) this.buildHtmlComment(msg);
            messages.push(msg);
          });
          this.activeThreadMessages = messages;
          resolve(messages);
        });
      });
    });
  }

  public justRecievedAddedMessage: any;
  watchThreadMessages(type, thread?) {
    // Unsubscribe from any existing subscription
    if (this.activeThreadMessagesSubscription) {
      this.activeThreadMessagesSubscription.unsubscribe();
    }

    this.activeThreadMessagesSubscription = this.fbService
      .handleFirestoreObservable(() =>
        this.db
          .collection("connection_threads")
          .doc(thread.id)
          .collection("messages", (ref) => ref.orderBy("timestamp", "desc"))
          .stateChanges()
          .pipe(
            map((data) => {
              data.forEach((itemDoc) => {
                let item: any = itemDoc.payload.doc.data();
                item.key = itemDoc.payload.doc.id;
                item.id = itemDoc.payload.doc.id;

                // Helper function to update a message
                const updateMessage = (msg) => {
                  msg.message = item.message;
                  if (msg.message) this.buildHtmlComment(msg);
                  if (item.edited) msg.edited = true;
                  return msg;
                };

                // Handle "added" messages
                if (itemDoc.type === "added") {
                  if (!item.timestamp) {
                    // Skip items without a `timestamp`
                    return;
                  }

                  // Add to activeThreadMessages if not already present
                  if (!this.activeThreadMessages.some((msg) => msg.id === item.id)) {
                    this.activeThreadMessages.push(updateMessage(item));
                    this.threadMessageEvent$.next("added");
                  }
                } else if (itemDoc.type === "removed") {
                  // Handle "removed" messages
                  const removeIndex = this.activeThreadMessages.findIndex((msg) => msg.id === item.id);
                  if (removeIndex !== -1) {
                    this.activeThreadMessages.splice(removeIndex, 1);
                    this.threadMessageEvent$.next("removed");
                  }
                } else if (itemDoc.type === "modified") {
                  // Handle "modified" messages
                  const existingMessage = this.activeThreadMessages.find((msg) => msg.id === item.id);

                  if (existingMessage) {
                    // Update existing message
                    updateMessage(existingMessage);
                    this.threadMessageEvent$.next("modified");
                  } else if (item.timestamp) {
                    // Add the message if it's not already present and has a valid timestamp
                    this.activeThreadMessages.push(updateMessage(item));
                    this.threadMessageEvent$.next("added");
                  }
                }
              });
              this.activeThreadMessages.sort((a, b) => {
                return a.timestamp.toDate() > b.timestamp.toDate() ? 1 : -1;
              });
              return this.activeThreadMessages;
            })
          )
      )
      .subscribe();
  }

  getPostByID(id, ignoreReplies = false, rejectWhenNotFound = true, collection: string = "community_posts", includeDeleted = false) {
    return new Promise((resolve, reject) => {
      let posts = [];
      this.fbService
        .handleFirestorePromise(() => this.db.collection(collection).doc(id).ref.get())
        .then((result) => {
          let post: any = result.data();
          if (post && (!post.deleted || includeDeleted)) {
            post.key = result.id;
            post.id = result.id;
            if (collection === "beta_community_posts" && !post.type) {
              post.type = post.parentID ? "reply" : "post";
              this.fbService.doc(`${collection}/${result.id}`).update({ type: post.type });
            }
            if (ignoreReplies) {
              resolve(this.buildHtmlComment(post));
            } else {
              let promises = [];
              posts.push(post);
              promises.push(
                this.fbService
                  .collection(collection)
                  .ref.where("parentID", "==", result.id)
                  .get()
                  .then((replies) => {
                    replies.docs.forEach((replyDoc) => {
                      let reply: any = replyDoc.data();
                      if (!reply.deleted || includeDeleted) {
                        reply.key = replyDoc.id;
                        reply.id = replyDoc.id;
                        if (collection === "beta_community_posts" && !reply.type) {
                          reply.type = "reply";
                          this.fbService.doc(`${collection}/${replyDoc.id}`).update({ type: reply.type });
                        }
                        posts.push(reply);
                      }
                    });
                  })
              );
              posts.forEach((item) => {
                this.buildHtmlComment(item);
              });
              return Promise.all(promises).then((result) => {
                // this.processPostsAndReplies(posts);
                resolve(posts);
              });
            }
          } else {
            if (rejectWhenNotFound) {
              reject(new Error("Post not found."));
            } else {
              resolve(null);
            }
          }
        });
    });
  }

  watchPostReplies(id, collection: string = "community_posts") {
    return this.fbService.handleFirestoreObservable(() =>
      this.db
        .collection(collection, (ref) => {
          return ref.where("parentID", "==", id).orderBy("created", "asc");
        })
        .snapshotChanges()
        .pipe(
          skip(1),
          map((result) => {
            let posts = [];
            result.forEach((itemDoc) => {
              let item: any = itemDoc.payload.doc.data();
              if (item.employee_only && !this.userService.isEmployee) {
                // dont add because its employee only
              } else if (!item.deleted) {
                item.key = itemDoc.payload.doc.id;
                item.id = itemDoc.payload.doc.id;
                if (collection === "beta_community_posts" && !item.type) {
                  item.type = "reply";
                  this.fbService.doc(`${collection}/${itemDoc.payload.doc.id}`).update({ type: item.type });
                }
                this.buildHtmlComment(item);
                posts.push(item);
              }
            });
            return posts;
          })
        )
    );
  }

  unwatchPostReplies() {
    this.activePostDetailSubscription.unsubscribe();
  }

  getBetaCommunityDetails(betaStringId) {
    // gets tag counts and author list

    return this.fbService
      .handleFirestorePromise(() => this.db.collection("beta_community_posts").ref.where("beta_string_id", "==", betaStringId).where("parentID", "==", null).get())
      .then((result) => {
        this.betaTagCountMap = {};
        this.betaAuthorList = [];
        this.betaAuthorMap = {};
        result.docs.forEach((doc) => {
          let data: any = doc.data();
          if (!data.deleted) {
            if (data.channels?.length) {
              data.channels.forEach((channel) => {
                if (!this.betaTagCountMap[channel]) this.betaTagCountMap[channel] = 0;
                this.betaTagCountMap[channel]++;
              });
            }
            this.betaAuthorList.push(data.author);
            this.betaAuthorMap[data.author.id] = data.author;
            this.betaAuthorList = Array.from(new Set(this.betaAuthorList.map((item) => item.id))).map((id) => this.betaAuthorList.find((item) => item.id === id));
            // now sort it by author.name
            this.betaAuthorList.sort((a, b) => (a.name > b.name ? 1 : -1));
          }
        });
      });
  }

  getPostsWithReplies(args: {
    channel?: any;
    topic?: any;
    postsAccountID?: string;
    moderationAccountID?: string;
    pins?: boolean;
    mostPopular?: boolean;
    limit?: number;
    clear?: boolean;
    collection?: string;
    betaStringId?: string;
  }) {
    if (!args.collection) args.collection = "community_posts";
    return super.getPostsWithReplies(args, this.fbService.collection(args.collection).ref);
  }

  public watchingCollectionRef: any;
  watchPostsWithReplies(channel?, topic?, postsAccountID?, pins?, collection: string = "community_posts", betaStringId?: string, watchPostsFrom?: Date) {
    if (this.activePostsSubscription) this.activePostsSubscription.unsubscribe();
    if (this.watchingCollectionRef) this.watchingCollectionRef.unsubscribe();
    this.activePostsSubscription = undefined;
    this.activePostsSubscription = this.fbService
      .handleFirestoreObservable(() => {
        return this.db
          .collection(collection, (ref) => {
            if (betaStringId) {
              if (channel && channel.title != "General" && channel.title != "All Posts" && channel.title != "My Pinned Posts") {
                return ref.where("channels", "array-contains-any", [channel.string_id]).where("beta_string_id", "==", betaStringId).orderBy("created", "asc");
              } else {
                if (watchPostsFrom) {
                  return ref.where("created", ">=", watchPostsFrom).where("beta_string_id", "==", betaStringId).orderBy("created", "asc");
                } else {
                  return ref.where("beta_string_id", "==", betaStringId).orderBy("created", "asc");
                }
              }
            } else {
              if (channel && channel.title != "General" && channel.title != "All Posts" && channel.title != "My Pinned Posts") {
                return ref.where("channels", "array-contains-any", [channel.string_id]).orderBy("created", "asc");
              } else if (topic) {
                return ref.where("topics", "array-contains-any", [topic.string_id]).orderBy("created", "asc");
              } else {
                let threeDaysAgo = new Date();
                threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
                return ref.where("created", ">=", threeDaysAgo).orderBy("created", "asc");
              }
            }
          })
          .stateChanges()
          .pipe(
            skip(1),
            map((result) => {
              if (result.length) {
                let posts = [];
                // this is admittedly odd, but when we initially watch posts we get an added event for all posts in the set. we only want to get updates on operations on individual posts, so
                // if its more than 5 its likely the first get. the skip for some reason is only working on page load (the first time we get the posts).
                if (result.length < 5) {
                  result.forEach((itemDoc) => {
                    let item: any = itemDoc.payload.doc.data();
                    this.pendingPosts = this.pendingPosts.filter((post) => post.subject != item.subject);
                    item.key = itemDoc.payload.doc.id;
                    item.id = itemDoc.payload.doc.id;
                    if ((itemDoc.type == "modified" && item.deleted) || itemDoc.type == "removed") {
                      if (item.parentID) {
                        let removeIndex;
                        if (this.activeParentThreadMap[item.parentID] && this.activeParentThreadMap[item.parentID].length) {
                          this.activeParentThreadMap[item.parentID].forEach((msg, i) => {
                            if (msg.id == item.id) removeIndex = i;
                          });
                          if (removeIndex || removeIndex === 0) this.activeParentThreadMap[item.parentID].splice(removeIndex, 1);
                        }
                      } else {
                        let removeIndex;
                        this.activeParentPosts.forEach((msg, i) => {
                          if (msg.id == item.id) removeIndex = i;
                        });
                        if (removeIndex || removeIndex === 0) this.activeParentPosts.splice(removeIndex, 1);
                      }
                    } else if (itemDoc.type == "added") {
                      if (pins && !item.parentID) {
                        // we are watching pins, and theres no way a new post could already be pinned, so ignore.
                      } else {
                        if (!postsAccountID || item.author.id == postsAccountID || item.parentID) {
                          if (item.employee_only && !this.userService.isEmployee) {
                            // dont add because its employee only
                          } else if (!topic && item.topics && item.topics.length) {
                            // dont add if we arent watching a topic, and this post has topics
                          } else {
                            this.buildHtmlComment(item);
                            if (!item.parentID) {
                              if (!this.activeParentThreadMap[item.key]) this.activeParentThreadMap[item.key] = [];
                              this.displayedPostsMap[item.key] = item;
                              // check if the post is already in activeParentPosts before adding it.
                              let alreadyAdded = false;
                              this.activeParentPosts.forEach((msg) => {
                                if (msg.id == item.id) alreadyAdded = true;
                              });
                              if (!alreadyAdded) {
                                this.activeParentPosts.push(item);
                                this.activeParentPosts.sort((a, b) => {
                                  if (a.pinned && !b.pinned) return -1;
                                  if (!a.pinned && b.pinned) return 1;
                                  const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
                                  const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
                                  return bCreated.getTime() - aCreated.getTime();
                                });
                              }
                            } else {
                              if (!this.activeParentThreadMap[item.parentID]) this.activeParentThreadMap[item.parentID] = [];
                              this.displayedPostsMap[item.key] = item;
                              // check if the post is already in the thread map array before adding it
                              let alreadyAdded = false;
                              this.activeParentThreadMap[item.parentID].forEach((msg) => {
                                if (msg.id == item.id) alreadyAdded = true;
                              });
                              if (!alreadyAdded) this.activeParentThreadMap[item.parentID].push(item);
                            }
                          }
                        }
                      }
                    } else if (itemDoc.type == "modified") {
                      if (!itemDoc.payload.doc.metadata.hasPendingWrites) {
                        const update = (msg) => {
                          msg.message = item.message;
                          msg.htmlMessage = item.htmlMessage;
                          this.buildHtmlComment(msg);
                          if (msg.file) msg.file.url = this.buildFileUrl(msg.file.user_id, msg.file);
                          if (item.subject) msg.subject = item.subject;
                          msg.beta_type = item.beta_type || "none";
                          msg.beta_status = item.beta_status || "none";
                          msg.beta_review_details = item.beta_review_details;
                          if (item.likes) msg.likes = item.likes;
                          if (item.pins) msg.pins = item.pins;
                          if (item.flags) msg.flags = item.flags;
                          if (item.cleared || item.cleared === false) msg.cleared = item.cleared;
                          if (item.edited) msg.edited = true;
                          if (item.articles) msg.articles = item.articles;
                          if (item.learn_content) msg.learn_content = item.learn_content;
                          if (item.categories_or_sections) msg.categories_or_sections = item.categories_or_sections;
                          if (item.pinned !== undefined) msg.pinned = item.pinned;
                        };
                        if (item.parentID) {
                          if (this.activeParentThreadMap[item.parentID] && this.activeParentThreadMap[item.parentID].length) {
                            this.activeParentThreadMap[item.parentID].forEach((msg) => {
                              if (msg.id == item.id) msg = update(msg);
                            });
                          }
                        } else {
                          this.activeParentPosts.forEach((msg) => {
                            if (msg.id == item.id) {
                              update(msg);
                            }
                          });
                          this.activeParentPosts.sort((a, b) => {
                            if (a.pinned && !b.pinned) return -1;
                            if (!a.pinned && b.pinned) return 1;
                            const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
                            const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
                            return bCreated.getTime() - aCreated.getTime();
                          });
                        }
                      }
                    }
                    for (var x in this.activeParentThreadMap) {
                      this.activeParentThreadMap[x].sort((a, b) => {
                        return a.created > b.created ? 1 : -1;
                      });
                    }
                  });
                }
                return posts;
              }
            })
          );
      })
      .subscribe();
  }

  /**
   * Watch individual displayed posts by their IDs.
   * This is more efficient than watching an entire collection query for large datasets.
   * Use this for beta communities with many posts to avoid performance issues.
   *
   * @param postIds Array of post IDs to watch
   * @param collection Firestore collection name
   * @param appendMode If true, adds watchers for new posts without clearing existing ones (for "load more")
   */
  watchDisplayedPosts(postIds: string[], collection: string = "community_posts", appendMode: boolean = false) {
    // If not in append mode, clear existing watchers first
    if (!appendMode) {
      this.unwatchDisplayedPosts();
    }

    // Get IDs we're already watching to avoid duplicates in append mode
    const watchedIds = new Set(this.displayedPostWatchers.map((w) => w.postId));

    postIds.forEach((postId) => {
      // Skip if already watching this post
      if (watchedIds.has(postId)) return;

      const subscription = this.fbService
        .handleFirestoreObservable(() =>
          this.db
            .collection(collection)
            .doc(postId)
            .snapshotChanges()
            .pipe(
              skip(1), // Skip initial snapshot, we already have the data from getPostsWithReplies
              map((snapshot) => {
                if (!snapshot.payload.exists) {
                  // Document was deleted
                  return { type: "removed", id: postId, data: null };
                }
                const data: any = snapshot.payload.data();
                data.key = snapshot.payload.id;
                data.id = snapshot.payload.id;
                return { type: "modified", id: postId, data };
              })
            )
        )
        .subscribe((result: any) => {
          if (result.type === "removed" || (result.data && result.data.deleted)) {
            // Handle removed/deleted post
            if (result.data?.parentID) {
              const removeIndex = this.activeParentThreadMap[result.data.parentID]?.findIndex((msg) => msg.id === result.id);
              if (removeIndex !== undefined && removeIndex !== -1) {
                this.activeParentThreadMap[result.data.parentID].splice(removeIndex, 1);
              }
            } else {
              const removeIndex = this.activeParentPosts.findIndex((msg) => msg.id === result.id);
              if (removeIndex !== -1) {
                this.activeParentPosts.splice(removeIndex, 1);
              }
            }
          } else if (result.type === "modified" && result.data) {
            const item = result.data;
            this.buildHtmlComment(item);

            const update = (msg) => {
              msg.message = item.message;
              msg.htmlMessage = item.htmlMessage;
              this.buildHtmlComment(msg);
              if (msg.file) msg.file.url = this.buildFileUrl(msg.file.user_id, msg.file);
              if (item.subject) msg.subject = item.subject;
              msg.beta_type = item.beta_type || "none";
              msg.beta_status = item.beta_status || "none";
              msg.beta_review_details = item.beta_review_details;
              if (item.likes) msg.likes = item.likes;
              if (item.pins) msg.pins = item.pins;
              if (item.flags) msg.flags = item.flags;
              if (item.cleared || item.cleared === false) msg.cleared = item.cleared;
              if (item.edited) msg.edited = true;
              if (item.articles) msg.articles = item.articles;
              if (item.learn_content) msg.learn_content = item.learn_content;
              if (item.categories_or_sections) msg.categories_or_sections = item.categories_or_sections;
              if (item.pinned !== undefined) msg.pinned = item.pinned;
            };

            if (item.parentID) {
              // This is a reply
              if (this.activeParentThreadMap[item.parentID]?.length) {
                this.activeParentThreadMap[item.parentID].forEach((msg) => {
                  if (msg.id === item.id) update(msg);
                });
              }
            } else {
              // This is a parent post
              this.activeParentPosts.forEach((msg) => {
                if (msg.id === item.id) update(msg);
              });
              this.activeParentPosts.sort((a, b) => {
                if (a.pinned && !b.pinned) return -1;
                if (!a.pinned && b.pinned) return 1;
                const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
                const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
                return bCreated.getTime() - aCreated.getTime();
              });
            }
          }
        });

      this.displayedPostWatchers.push({ postId, subscription });
    });
  }

  /**
   * Unsubscribe from all individual post watchers
   */
  unwatchDisplayedPosts() {
    this.displayedPostWatchers.forEach((watcher) => {
      if (watcher.subscription) {
        watcher.subscription.unsubscribe();
      }
    });
    this.displayedPostWatchers = [];
  }

  public newPostsSubscription: any;
  /**
   * Watch for new posts created after a given timestamp.
   * This is used alongside watchDisplayedPosts to catch new posts/replies.
   * The query initially matches 0 documents and only picks up newly created posts.
   *
   * @param watchFrom Timestamp to watch from (typically "now" when page loads)
   * @param collection Firestore collection name
   * @param betaStringId Beta program string ID to filter by
   * @param channel Optional channel to filter by
   */
  watchNewPosts(watchFrom: Date, collection: string = "community_posts", betaStringId?: string, channel?: any) {
    if (this.newPostsSubscription) this.newPostsSubscription.unsubscribe();

    this.newPostsSubscription = this.fbService
      .handleFirestoreObservable(() => {
        return this.db
          .collection(collection, (ref) => {
            if (betaStringId) {
              if (channel && channel.title != "General" && channel.title != "All Posts" && channel.title != "My Pinned Posts") {
                return ref.where("channels", "array-contains-any", [channel.string_id]).where("beta_string_id", "==", betaStringId).where("created", ">", watchFrom).orderBy("created", "asc");
              } else {
                return ref.where("beta_string_id", "==", betaStringId).where("created", ">", watchFrom).orderBy("created", "asc");
              }
            } else {
              return ref.where("created", ">", watchFrom).orderBy("created", "asc");
            }
          })
          .stateChanges()
          .pipe(
            map((result) => {
              result.forEach((itemDoc) => {
                let item: any = itemDoc.payload.doc.data();
                item.key = itemDoc.payload.doc.id;
                item.id = itemDoc.payload.doc.id;

                if (itemDoc.type === "added") {
                  // Remove from pending posts (the queued post with spinner)
                  this.pendingPosts = this.pendingPosts.filter((post) => post.subject != item.subject);

                  if (item.employee_only && !this.userService.isEmployee) {
                    // dont add because its employee only
                  } else {
                    this.buildHtmlComment(item);
                    if (!item.parentID) {
                      // New parent post
                      if (!this.activeParentThreadMap[item.key]) this.activeParentThreadMap[item.key] = [];
                      this.displayedPostsMap[item.key] = item;
                      // Check if already added
                      let alreadyAdded = this.activeParentPosts.some((msg) => msg.id === item.id);
                      if (!alreadyAdded) {
                        this.activeParentPosts.push(item);
                        this.activeParentPosts.sort((a, b) => {
                          if (a.pinned && !b.pinned) return -1;
                          if (!a.pinned && b.pinned) return 1;
                          const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
                          const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
                          return bCreated.getTime() - aCreated.getTime();
                        });
                        // Also start watching this new post for modifications
                        this.watchDisplayedPosts([item.id], collection, true);
                      }
                    } else {
                      // New reply
                      if (!this.activeParentThreadMap[item.parentID]) this.activeParentThreadMap[item.parentID] = [];
                      this.displayedPostsMap[item.key] = item;
                      let alreadyAdded = this.activeParentThreadMap[item.parentID].some((msg) => msg.id === item.id);
                      if (!alreadyAdded) {
                        this.activeParentThreadMap[item.parentID].push(item);
                        this.activeParentThreadMap[item.parentID].sort((a, b) => {
                          return a.created > b.created ? 1 : -1;
                        });
                        // Also start watching this new reply for modifications
                        this.watchDisplayedPosts([item.id], collection, true);
                      }
                    }
                  }
                }
              });
              return result;
            })
          );
      })
      .subscribe();
  }

  /**
   * Unsubscribe from new posts watcher
   */
  unwatchNewPosts() {
    if (this.newPostsSubscription) {
      this.newPostsSubscription.unsubscribe();
      this.newPostsSubscription = undefined;
    }
  }

  unwatch() {
    this.displayedPostsMap = {};
    this.activeParentPosts = [];
    this.activeParentThreadMap = {};

    this.activeThreadMessages = [];
    if (this.activeThreadMessagesSubscription) this.activeThreadMessagesSubscription.unsubscribe();
    if (this.activePostsSubscription) this.activePostsSubscription.unsubscribe();
    this.unwatchDisplayedPosts();
    this.unwatchNewPosts();
  }

  public comments: Array<any> = [];
  public replies: Array<any> = [];
  public parentThreadMap: any = {};
  public processedCommentMap: any = {};
  public commentSubscription: any;
  public subscribeToComments(ref, sortOrder?) {
    return ref.stateChanges().pipe(
      map((data: any) => {
        data.forEach((itemDoc) => {
          let item: any = itemDoc.payload.doc.data();
          item.key = itemDoc.payload.doc.id;
          item.id = itemDoc.payload.doc.id;

          // Ensure parent thread map is initialized
          if (!item.parentID && !this.parentThreadMap[item.id]) this.parentThreadMap[item.id] = [];

          // Build HTML for the comment
          item = this.buildHtmlComment(item);

          // Handle "added" comments
          if (itemDoc.type === "added") {
            if (!item.created && !item.timestamp) {
              // Skip items without a `created` timestamp
              return;
            }

            if (!item.deleted && !item.parentID) {
              // Add to comments if not already present
              if (!this.comments.some((msg) => msg.id === item.id)) {
                this.comments.push(item);
              }
            } else if (!item.deleted && item.parentID) {
              // Add to replies if not already present
              if (!this.replies.some((msg) => msg.id === item.id)) {
                this.replies.push(item);
              }
            }
          } else if (itemDoc.type === "removed") {
            // Handle "removed" comments
            this.performDeleteComment(item);
          } else if (itemDoc.type === "modified") {
            // Handle "modified" comments
            const updateComment = (msg) => {
              msg.message = item.message;
              msg.htmlMessage = item.htmlMessage;
              msg.likes = item.likes;
              msg.flags = item.flags;
              this.buildHtmlComment(msg);
              if (item.edited) msg.edited = true;
            };

            if (item.deleted) {
              this.performDeleteComment(item);
            } else {
              let found = false;

              // Update or add to comments
              if (!item.parentID) {
                this.comments.forEach((msg) => {
                  if (msg.id === item.id) {
                    found = true;
                    updateComment(msg);
                  }
                });
              }

              // Update or add to replies
              else if (this.parentThreadMap[item.parentID]) {
                this.parentThreadMap[item.parentID].forEach((msg) => {
                  if (msg.id === item.id) {
                    found = true;
                    updateComment(msg);
                  }
                });
              }

              if (!found) {
                if (!item.deleted && !item.parentID) {
                  this.comments.push(item);
                } else if (!item.deleted && item.parentID) {
                  this.replies.push(item);
                }
              }
            }
          }
        });

        // Process replies
        this.replies.forEach((item) => {
          if (!this.processedCommentMap[item.id]) {
            this.processedCommentMap[item.id] = true;
            if (this.parentThreadMap[item.parentID]) {
              this.parentThreadMap[item.parentID].push(item);
            }
          }
        });

        // Sort comments
        let sortInt = sortOrder === "asc" ? -1 : 1;
        this.comments.sort((a, b) => {
          return b.created > a.created ? sortInt : -sortInt;
        });

        return this.comments;
      })
    );
  }

  performDeleteComment(item) {
    if (item.parentID) {
      let deleteIndex;
      this.parentThreadMap[item.parentID].forEach((msg, index) => {
        if (msg.id == item.id) deleteIndex = index;
      });
      this.parentThreadMap[item.parentID].splice(deleteIndex, 1);
    } else {
      let deleteIndex;
      this.comments.forEach((msg, index) => {
        if (msg.id == item.id) deleteIndex = index;
      });
      if (deleteIndex || deleteIndex === 0) this.comments.splice(deleteIndex, 1);
    }
  }

  deletePost(id, admin?, collection: string = "community_posts") {
    let args: any = {
      deleted: {
        timestamp: new Date(),
        deleted_by: this.userService.user.id,
      },
    };
    if (admin) args.deleted.admin = true;
    return this.fbService.doc(`${collection}/${id}`).update(args);
  }

  likeUnlikePost(post, isLiking?, collection: string = "community_posts") {
    if (isLiking) {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        likes: this.fbService.arrayUnion(this.userService.user.id),
        likeCount: post.likes && post.likes.length ? post.likes.length + 1 : 1,
      });
    } else {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        likes: this.fbService.arrayRemove(this.userService.user.id),
        likeCount: post.likes.length - 1,
      });
    }
  }

  pinUnpinPost(post, collection: string = "community_posts") {
    if (post.pins && post.pins.includes(this.userService.user.id)) {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        pins: this.fbService.arrayRemove(this.userService.user.id),
      });
    } else {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        pins: this.fbService.arrayUnion(this.userService.user.id),
      });
    }
  }

  adminPinUnpinPost(post, collection: string = "community_posts") {
    const isBetaManager = this.userService.user.groups.some((group: any) => group.name === "beta_manager");
    if (!isBetaManager) {
      return Promise.reject(new Error("Only beta managers can pin/unpin posts."));
    }

    return this.fbService.doc(`${collection}/${post.key}`).update({
      pinned: post.pinned ? false : true,
    });
  }

  markUnmarkSphereOnly(post, collection: string = "community_posts") {
    return this.fbService.doc(`${collection}/${post.key}`).update({
      sphere_only: post.sphere_only ? false : true,
    });
  }

  flagUnflagPost(post, reason?, collection: string = "community_posts") {
    if (post.flags && post.flags.includes(this.userService.user.id)) {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        flags: this.fbService.arrayRemove(this.userService.user.id),
      });
    } else {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        latest_flag_reason: reason,
        cleared: false,
        flags: this.fbService.arrayUnion(this.userService.user.id),
      });
    }
  }

  clearFlag(id, collection: string = "community_posts") {
    return this.fbService.doc(`${collection}/${id}`).update({ cleared: true });
  }

  updatePost(id, args, collection: string = "community_posts") {
    return super.updatePost(this.fbService.doc(`${collection}/${id}`), args);
  }

  getConnectedUserMessageThreads() {
    return super.getConnectedUserMessageThreads(this.fbService.collection("connection_threads").ref);
  }

  watchMissedMessageCounts() {
    if (this.missedMessageSubscription) this.missedMessageSubscription.unsubscribe();
    this.missedMessageSubscription = this.fbService
      .handleFirestoreObservable(() =>
        this.db
          .collection("connection_threads", (ref) => {
            return ref.where("users", "array-contains-any", [this.userService.user.id]);
          })
          .snapshotChanges()
          .pipe(
            throttle(
              () => {
                return interval(500);
              },
              { leading: true, trailing: true }
            )
          )
      )
      .subscribe((result: any) => {
        if (!this.userService.user) {
          if (this.missedMessageSubscription) this.missedMessageSubscription.unsubscribe();
        } else {
          this.unreadCounts.total = 0;
          this.unreadCounts.threads = {};
          result.forEach((item) => {
            let data: any = item.payload.doc.data();
            if (!data.inactive || !data.inactive.length) {
              let docID = item.payload.doc.id;
              if (data.viewing) {
                let obj = {};
                data.viewing.forEach((id) => {
                  obj[id] = true;
                });
                this.viewing[docID] = obj;
              }
              if (data.missedMessages) {
                if (!this.activeThread || this.activeThread.id != docID) {
                  this.unreadCounts.total += data.missedMessages[this.userService.user.id] ? data.missedMessages[this.userService.user.id] : 0;
                  this.unreadCounts.threads[docID] = data.missedMessages[this.userService.user.id];
                }
              }
            }
          });
        }
      });
  }

  getUnreadNotificationCount() {
    return super.getUnreadNotificationCount(
      this.db
        .collection("user_profiles")
        .doc(this.userService.user.id)
        .collection("notifications", (ref) => {
          return ref.limit(100).orderBy("created");
        }).ref
    );
  }

  listenNotificationUpdates() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    this.notificationSubscription = this.fbService
      .handleFirestoreObservable(() =>
        this.db
          .collection("user_profiles")
          .doc(this.userService.user.id)
          .collection("notifications")
          .snapshotChanges()
          .pipe(
            map((changes: any) =>
              changes.map((c) => {
                let rtn = { ...c.payload.doc.data(), key: c.payload.doc.id };
                return rtn;
              })
            )
          )
      )
      .subscribe((result: any) => {
        if (!this.userService.user) {
          this.notificationSubscription.unsubscribe();
        } else {
          this.notificationCount = result.length;
          this.unreadNotificationCount = 0;
          result.forEach((data) => {
            if (!data.seen) this.unreadNotificationCount++;
          });
        }
      });
  }

  getNotifications() {
    return super.getNotifications(
      this.fbService.collection(`user_profiles/${this.userService.user.id}/notifications`, (ref) => {
        return ref.limit(100).orderBy("created");
      }).ref
    );
  }

  deleteNotification(id) {
    return super.deleteNotification(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`)).then(() => {
      return super
        .updateNotification(this.fbService.doc(`user_profiles/${this.userService.user.id}`), {
          notificationsTag: this.fbService.increment(1),
        })
        .then(() => {
          this.getUnreadNotificationCount();
        });
    });
  }

  batchDeleteNotifications(ids: Array<string>) {
    let batch = this.db.firestore.batch();
    ids.forEach((id) => {
      batch.delete(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`).ref);
    });
    return batch.commit().then(() => {
      return this.fbService
        .doc(`user_profiles/${this.userService.user.id}`)
        .update({
          notificationsTag: this.fbService.increment(1),
        })
        .then(() => {
          this.getUnreadNotificationCount();
        });
    });
  }

  batchUpdateNotifications(ids: Array<string>, data: any) {
    let batch = this.db.firestore.batch();
    ids.forEach((id) => {
      batch.update(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`).ref, data);
    });
    return batch.commit().then(() => {
      this.getUnreadNotificationCount();
    });
  }

  updateNotification(id, data) {
    super.updateNotification(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`), data).then(() => {
      this.getUnreadNotificationCount();
    });
  }

  getTopic(string_id) {
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("community_topics").doc(string_id).ref.get())
      .then((result) => {
        if (result.exists) {
          return result.data();
        } else {
          return false;
        }
      });
  }

  getTopics(all?, includeArchived = true) {
    let query: firebase.firestore.Query;

    if (all) {
      query = this.fbService.collection("community_topics").ref;
    } else if (includeArchived) {
      query = this.fbService.collection("community_topics").ref.where("start_date", "<", new Date());
    } else {
      query = this.fbService.collection("community_topics").ref.where("archived", "==", false).where("start_date", "<", new Date());
    }

    return this.fbService
      .handleFirestorePromise(() => query.get())
      .then((result) => {
        let rtn = [];
        if (!result.empty) {
          result.docs.forEach((topic) => {
            let data: any = topic.data();
            this.topicMap[data.string_id] = data;
            rtn.push(data);
          });
        }
        this.topics = rtn;
        return rtn;
      });
  }

  addTopic(data) {
    data.created_by = this.userService.user.id;
    data.created = new Date();
    data.archived = false;
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("community_topics").doc(data.string_id).set(data))
      .then(() => {
        this.getTopics();
      });
  }

  updateTopic(string_id, data) {
    return this.fbService
      .doc(`community_topics/${string_id}`)
      .update(data)
      .then(() => {
        this.getTopics();
      });
  }

  toggleArchiveTopic(string_id, archived) {
    return this.fbService
      .doc(`community_topics/${string_id}`)
      .update({ archived: archived, date_archived: new Date(), archived_by: this.userService.user.id })
      .then(() => {
        this.getTopics();
      });
  }

  createConnectedUserMessageThread(user_id) {
    return super.createConnectedUserMessageThread(this.fbService.collection("connection_threads").ref, user_id);
  }

  removeConnection(connectionId, user_id) {
    return super.removeConnection(connectionId).pipe(
      map((result) => {
        this.fbService
          .collection("connection_threads")
          .ref.where("users", "in", [[this.userService.user.id, user_id]])
          .get()
          .then((threadResult) => {
            if (threadResult.docs && threadResult.docs.length) {
              threadResult.docs.forEach((doc) => {
                let data: any = doc.data();
                let connection = data.users.filter((id) => id == user_id)[0];
                if (connection) {
                  doc.ref.update({
                    inactive: this.fbService.arrayUnion(this.userService.user.id),
                  });
                }
              });
            }
          });
        return result;
      })
    );
  }

  getAnnouncements() {
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("sphere_announcements").ref.get())
      .then((result) => {
        let announcements = [];
        result.docs.forEach((doc: any) => {
          let data = doc.data();
          announcements.push({
            ...data,
            id: doc.id,
          });
        });
        return announcements;
      });
  }

  deleteAnnouncement(id) {
    return this.fbService.doc(`sphere_announcements/${id}`).delete();
  }

  addAnnouncement(id, args) {
    return this.fbService.doc(`sphere_announcements/${id}`).set(args);
  }

  addBetaAnnouncement(betaStringId, announcementStringId, args) {
    return this.fbService.doc(`beta_communities/${betaStringId}/announcements/${announcementStringId}`).set(args);
  }

  getSplashes() {
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("product_splashes").ref.get())
      .then((result) => {
        let splashes = [];
        result.docs.forEach((doc: any) => {
          let data = doc.data();
          splashes.push({
            ...data,
            id: doc.id,
          });
        });
        return splashes;
      });
  }

  deleteSplash(id) {
    return this.fbService.doc(`product_splashes/${id}`).delete();
  }

  addSplash(id, args) {
    return this.fbService.doc(`product_splashes/${id}`).set(args);
  }

  addNotification(user_id, notificationData) {
    notificationData.created = new Date();
    notificationData.seen = false;
    return this.fbService.collection(`user_profiles/${user_id}/notifications`).add(notificationData);
  }

  getStats(collection: string = "community_posts") {
    let promises = [];
    let stats = [];
    promises.push(
      this.fbService
        .collection(collection)
        .ref.where("parentID", "==", null)
        .get()
        .then((result) => {
          stats.push({
            title: "Original Posts",
            value: result.size,
          });
          let authors: any = {};
          let uniqueAuthorCount = 0;
          result.docs.forEach((doc) => {
            let data: any = doc.data();
            if (!authors[data.author.id]) {
              authors[data.author.id] = 0;
              uniqueAuthorCount++;
            }
            authors[data.author.id]++;
          });
          stats.push({
            title: "Unique Authors",
            value: uniqueAuthorCount,
            data: {
              description: "Post count per user ID.",
              values: authors,
            },
          });
        }),
      this.fbService
        .collection(collection)
        .ref.where("parentID", "!=", null)
        .get()
        .then((result) => {
          stats.push({
            title: "Replies",
            value: result.size,
          });
        }),
      this.fbService
        .collection("user_profiles")
        .ref.where("hasSetupCommunity", "==", true)
        .get()
        .then((result) => {
          stats.push({
            title: "Total Community Members",
            value: result.size,
          });
        }),
      this.fbService
        .collection("user_profiles")
        .ref.where("hasSetupCommunity", "==", true)
        .where("public", "==", true)
        .get()
        .then((result) => {
          stats.push({
            title: "Public Members",
            value: result.size,
          });
        }),
      this.fbService
        .collection("video_comments")
        .ref.get()
        .then((result) => {
          let totalComments = 0;
          let subPromises = [];
          let videoComments: Array<any> = [];
          result.docs.forEach((doc) => {
            subPromises.push(
              doc.ref
                .collection("comments")
                .get()
                .then((result) => {
                  if (result.docs.length) {
                    videoComments.push({
                      comments: result.docs.length,
                      title: (<any>doc.data()).title,
                      id: doc.id,
                    });
                    totalComments += result.docs.length;
                  }
                })
            );
          });
          return Promise.all(subPromises).then(() => {
            videoComments.sort((a, b) => {
              return a.comments > b.comments ? -1 : 1;
            });
            stats.push({
              title: "Total Video Comments",
              value: totalComments,
              data: {
                description: "Comment counts by video",
                values: videoComments,
              },
            });
          });
        })
    );
    return new Promise((resolve, reject) => {
      Promise.all(promises).then((result) => {
        resolve(stats);
      });
    });
  }

  parseEmbedUrls(text: string, compareArray?: MessageEmbed[]): MessageEmbed[] {
    const embeds = super.parseEmbedUrls(text, compareArray);
    if (embeds) {
      return embeds.map((embed) => ({
        ...embed,
        embedSrc: this._sanitizer.bypassSecurityTrustResourceUrl(embed.embedSrc as string),
      }));
    } else {
      return [];
    }
  }
}
